import {brandUpdateT} from '@tnc/shared';

import {FormFieldT, FormStepT} from '@/lib/types/form.types';

import {US_STATES_DATA} from '@/lib/jsonData/usStatesData';

/****************************************************************************
 * Form Fields
 ****************************************************************************/
export const brandFormFields: FormFieldT<brandUpdateT>[] = [
  {
    label: 'Brand Name',
    name: 'brand_name',
    options: {
      required: 'Brand name is required',
    },
  },
  {
    label: 'Brand Description',
    name: 'brand_description',
    type: 'textarea',
    options: {required: 'Brand description is required'},
  },
  {
    label: 'Logo Url',
    placeholder: 'Logo Url ex: https://example.com/logo.png',
    name: 'brand_logo_url',
  },
  {
    label: 'Brand Phone',
    placeholder: 'Phone Number ex: +1-805-555-1212',
    type: 'tel',
    name: 'brand_phone',
    options: {
      required: 'Phone number is required',
      pattern: {
        // Supports US and international phone numbers with or without country code
        value: /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
        message: 'Invalid phone number',
      },
    },
  },
  {
    label: 'Same As Speculator Email',
    name: 'brand_same_as_speculator_email',
    custom: true,
    type: 'checkbox',
    defaultValue: true,
    watch: {
      enabled: true,
      onChange: ({value, setWatchEnabled}) => {
        setWatchEnabled(value === true);
      },
      configs: [
        {
          source: 'speculator_email',
          target: 'brand_email',
          autoCopy: true,
        },
      ],
    },
  },
  {
    label: 'Brand Email',
    name: 'brand_email',
    type: 'email',
    options: {required: 'Brand email is required'},
    // -------------- ----------------------------------------------
    // Watch Configuration (Basic - Single Config)
    // ------------------------------------------------------------
    // Here we are disabling the brand email field when the same as speculator email
    // is checked
    // *Note: These (the watchConfig property) is ALWAYS enabled!!
    // *Note: There is also a copyFrom config that is used to copy a value from another field
    // ------------------------------------------------------------
    watchConfig: {
      disableWhen: {
        field: 'brand_same_as_speculator_email',
        is: true,
      },
    },
  },
  // ------------------------------------------------------------
  // Example of a custom form input
  // ------------------------------------------------------------
  {
    type: 'checkbox',
    // Defines that this is a custom form input and allows for custom logic/name
    custom: true,
    // The label for the form input - can be any string
    label: 'Same as Speculator Address',
    // Can be any string, but must be unique
    name: 'brand_same_as_speculator_address',
    // The default value for the field, note this is not "bound" to the default
    // value of the watch config, it is just the default value for the field
    defaultValue: true,
    // -------------- ----------------------------------------------
    // React Use Form Options
    // ------------------------------------------------------------
    options: {
      /**
       * The React Use Form onChange Event
       * This is the form input's change event, simply here for example
       * @param {React.ChangeEvent<{INPUT TYPE}>} event - The event passed to the onChange event
       * @returns {void}
       * @example
       * onChange(event: React.ChangeEvent<HTMLInputElement>) {
       *   console.log('use react form change event', event);
       * }
       */
      // onChange: (event: React.ChangeEvent<HTMLInputElement>) => {...}
    },
    // -------------- ----------------------------------------------
    // Watch Configurations (Advanced - Multiple Configs)
    // ------------------------------------------------------------
    watch: {
      // The default state for the watch configs (e.g. watching or not)
      enabled: true,
      /**
       * The watch onChangeEvent
       * This is a function that will be run when the form input changes
       * @param {OnChangePropsT} props - The props passed to the onChange event
       * @returns {void}
       */
      onChange: ({value, setWatchEnabled}) => {
        setWatchEnabled(value === true);
      },
      // ------------------------------------------------------------
      // Watch Configs
      // This is an array of WatchConfigT
      // ------------------------------------------------------------
      configs: [
        // Adding a target with no effect will "auto copy" the source value to the target
        {source: 'speculator_address', target: 'brand_address'},
        {source: 'speculator_address2', target: 'brand_address2'},
        {source: 'speculator_city', target: 'brand_city'},
        {source: 'speculator_state', target: 'brand_state'},
        // Auto copy is defaulted to true, but we can override it with a property
        {source: 'speculator_postal_code', target: 'brand_postal_code', autoCopy: true},
        // Note: we are copying the postal code below in the "basic" watch config {~178} ---->
        // -------------- ----------------------------------------------
        // A custom effect example
        // ------------------------------------------------------------
        // {
        //   source: 'speculator_address',
        //   target: 'brand_address',
        //   /**
        //    * Optional: The watch effect
        //    * This is a function that will be run when the form input changes
        //    * @param {WatchEffectPropsT} props - The props passed to the effect
        //    * @returns {void}
        //    * @example
        //    * effect: ({target, value, setFieldDisabled, setTarget}) => {
        //    *   // Optional: Preform some logic, in this case we are disabling the watch
        //    *   // setWatchEnabled(false);
        //    *
        //    *   // If the target is defined, set the target to the value,
        //    *   // similar to the default target copy effect, note we can control the
        //    *   // validation and dirty state via react use form options
        //    *   if (target) setTarget(value, {shouldValidate: false, shouldDirty: true});
        //    *
        //    *   // You can set the value of another field
        //    *   // setFieldValue('speculator_name_last', value);
        //    *
        //    *   // Set the disabled state for the field
        //    *   setDisabled(true);
        //    * }
        //    */
        //   effect: ({target, value, setTarget}) => {
        //     if (target) setTarget(value, {shouldValidate: false, shouldDirty: true});
        //   },
        // },
      ],
    },
  },
  {
    label: 'Address 1',
    name: 'brand_address',
    options: {required: 'Address is required'},
    watchConfig: {
      disableWhen: {
        field: 'brand_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Address 2',
    name: 'brand_address2',
    watchConfig: {
      disableWhen: {
        field: 'brand_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'City',
    name: 'brand_city',
    options: {required: 'City is required'},
    watchConfig: {
      disableWhen: {
        field: 'brand_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'State',
    type: 'select',
    name: 'brand_state',
    placeholder: 'Select State',
    selectOptions: US_STATES_DATA,
    options: {
      required: 'State is required',
      validate: value => !!US_STATES_DATA.find(state => state.value === value),
    },
    watchConfig: {
      disableWhen: {
        field: 'brand_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Postal Code',
    name: 'brand_postal_code',
    options: {
      required: 'Postal code is required',
      pattern: {value: /^\d{5}$/, message: 'Invalid postal code'},
    },
    watchConfig: {
      disableWhen: {
        field: 'brand_same_as_speculator_address',
        is: true,
      },
    },
  },
];

export type BrandFieldNamesT = (typeof brandFormFields)[number]['name'];

/****************************************************************************
 * Form Steps
 ****************************************************************************/
export const brandFormSteps: FormStepT<BrandFieldNamesT> = [
  {
    step: 0,
    name: 'personal',
    label: 'Brand Information',
    fields: ['brand_same_as_speculator_email', 'brand_email', 'brand_name', 'brand_logo_url', 'brand_description'],
  },
  {
    step: 1,
    name: 'address',
    label: 'Brand Address',
    fields: [
      'brand_same_as_speculator_address',
      'brand_address',
      'brand_address2',
      'brand_city',
      'brand_state',
      'brand_postal_code',
    ],
  },
];
